import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ParticleBackground from "@/components/ParticleBackground";
import AIGrid from "@/components/AIGrid";
import ProductDemo from "@/components/ProductDemo";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mic, BarChart3, Zap, Database, CheckCircle, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import voiceAIPlatform from "@/assets/product_images/holionix_voiceai_platform.jpg";
import analyticsImage1 from "@/assets/product_images/analytics.jpg";
import analyticsImage2 from "@/assets/product_images/analytics2.jpg";
import outboundImage from "@/assets/product_images/outbound.png";
import crmIntegration from "@/assets/product_images/holionix_integrationhub.jpg";
import { useTranslation } from 'react-i18next';

const Products = () => {
  const { t } = useTranslation();
  
  const products = [
    {
      icon: Mic,
      name: t('products.voice_platform_name'),
      image: voiceAIPlatform,
      description: t('products.voice_platform_desc'),
      features: [
        t('products.voice_platform_feature_1'),
        t('products.voice_platform_feature_2'),
        t('products.voice_platform_feature_3'),
        t('products.voice_platform_feature_4'),
        t('products.voice_platform_feature_5'),
        t('products.voice_platform_feature_6'),
      ],
      audience: t('products.voice_platform_audience'),
      benefit: t('products.voice_platform_benefit'),
      metrics: [t('products.voice_platform_metric_1'), t('products.voice_platform_metric_2'), t('products.voice_platform_metric_3')],
      demoType: "voice" as const,
    },
    {
      icon: BarChart3,
      name: t('products.analytics_name'),
      images: [analyticsImage1, analyticsImage2],
      description: t('products.analytics_desc'),
      features: [
        t('products.analytics_feature_1'),
        t('products.analytics_feature_2'),
        t('products.analytics_feature_3'),
        t('products.analytics_feature_4'),
        t('products.analytics_feature_5'),
        t('products.analytics_feature_6'),
        t('products.analytics_feature_7'),
      ],
      audience: t('products.analytics_audience'),
      benefit: t('products.analytics_benefit'),
      metrics: [t('products.analytics_metric_1'), t('products.analytics_metric_2'), t('products.analytics_metric_3')],
      demoType: "analytics" as const,
    },
    {
      icon: Zap,
      name: t('products.outbound_name'),
      image: outboundImage,
      description: t('products.outbound_desc'),
      features: [
        t('products.outbound_feature_1'),
        t('products.outbound_feature_2'),
        t('products.outbound_feature_3'),
        t('products.outbound_feature_4'),
        t('products.outbound_feature_5'),
        t('products.outbound_feature_6'),
      ],
      audience: t('products.outbound_audience'),
      benefit: t('products.outbound_benefit'),
      metrics: [t('products.outbound_metric_1'), t('products.outbound_metric_2'), t('products.outbound_metric_3')],
      demoType: "outbound" as const,
    },
    {
      icon: Database,
      name: t('products.integration_name'),
      image: crmIntegration,
      description: t('products.integration_desc'),
      features: [
        t('products.integration_feature_1'),
        t('products.integration_feature_2'),
        t('products.integration_feature_3'),
        t('products.integration_feature_4'),
        t('products.integration_feature_5'),
        t('products.integration_feature_6'),
      ],
      audience: t('products.integration_audience'),
      benefit: t('products.integration_benefit'),
      metrics: [t('products.integration_metric_1'), t('products.integration_metric_2'), t('products.integration_metric_3')],
      demoType: "integration" as const,
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <ParticleBackground />
      <AIGrid />
      <Navigation />
      
      <main className="flex-grow pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 gradient-brand-text tracking-tight">{t('products.title')}</h1>
            <p className="text-2xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
              {t('products.subtitle')}
            </p>
          </motion.div>

          <div className="space-y-32 max-w-7xl mx-auto">
            {products.map((product, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.15 }}
                className="space-y-12"
              >
                <div className={`grid md:grid-cols-12 gap-8 items-start ${index % 2 === 1 ? 'md:grid-flow-dense' : ''}`}>
                  {/* Content Side - Now on top/left with better proportions */}
                  <div className={`md:col-span-5 space-y-8 ${index % 2 === 1 ? 'md:col-start-8' : 'md:col-start-1'} md:row-start-1 flex flex-col justify-start`}>
                    <div>
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-2xl glow-effect flex-shrink-0">
                          <product.icon className="w-10 h-10 text-white" />
                        </div>
                        <h2 className="text-2xl md:text-3xl font-bold tracking-tight leading-tight">{product.name}</h2>
                      </div>
                      <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8">{product.description}</p>
                    </div>
                    
                    <Card className="p-6 glass-effect border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5">
                      <h3 className="font-bold mb-4 flex items-center gap-2 text-lg">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        {t('products.features_label')}
                      </h3>
                      <ul className="space-y-2">
                        {product.features.slice(0, 4).map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-muted-foreground text-sm md:text-base">
                            <div className="w-2 h-2 rounded-full bg-primary mt-2.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>
                    
                    <div className="flex flex-wrap gap-3">
                      {product.metrics.map((metric, idx) => (
                        <motion.span
                          key={idx}
                          initial={{ scale: 0.9, opacity: 0 }}
                          whileInView={{ scale: 1, opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: idx * 0.1 }}
                          className="px-5 py-2 rounded-lg bg-gradient-to-r from-primary/20 to-accent/20 border-2 border-primary/30 font-bold text-xs md:text-sm"
                        >
                          {metric}
                        </motion.span>
                      ))}
                    </div>
                    
                    <Card className="p-6 glass-effect border-green-500/30 bg-gradient-to-r from-green-500/10 to-emerald-500/10">
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                        <p className="text-muted-foreground font-semibold text-sm md:text-base leading-relaxed">{product.benefit}</p>
                      </div>
                    </Card>
                  </div>

                  {/* Image Side - Larger and more prominent */}
                  <div className={`md:col-span-7 ${index % 2 === 1 ? 'md:col-start-1 md:row-start-1' : 'md:col-start-6'}`}>
                    {product.images ? (
                      // Vertical stacked layout for Analytics - Larger images
                      <div className="space-y-6">
                        {product.images.map((img, imgIdx) => (
                          <Card key={imgIdx} className="overflow-hidden glass-effect border-primary/40 hover-lift group shadow-2xl">
                            <div className="relative w-full h-80 md:h-96 overflow-hidden">
                              <motion.img 
                                whileHover={{ scale: 1.05 }}
                                transition={{ duration: 0.6, ease: "easeOut" }}
                                src={img} 
                                alt={`${product.name} - Image ${imgIdx + 1}`}
                                className="w-full h-full object-contain"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
                              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                            </div>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      // Single image layout for other products - Much larger
                      <Card className="overflow-hidden glass-effect border-primary/40 hover-lift group shadow-2xl">
                        <div className="relative h-96 md:h-[32rem] overflow-hidden">
                          <motion.img 
                            whileHover={{ scale: 1.05 }}
                            transition={{ duration: 0.6, ease: "easeOut" }}
                            src={product.image} 
                            alt={product.name}
                            className="w-full h-full object-contain"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
                          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                          <div className="absolute top-6 right-6">
                            <motion.div 
                              initial={{ scale: 0.8, opacity: 0 }}
                              whileInView={{ scale: 1, opacity: 1 }}
                              transition={{ type: "spring", stiffness: 200 }}
                              className="px-4 py-2 rounded-full gradient-brand text-white text-xs md:text-sm font-bold flex items-center gap-2 shadow-2xl"
                            >
                              <Sparkles className="w-4 h-4" />
                              {t('products.featured_product')}
                            </motion.div>
                          </div>
                        </div>
                      </Card>
                    )}
                  </div>
                </div>

                {/* Interactive Demo Section */}
                <div className="mt-16">
                  <ProductDemo productType={product.demoType} />
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-20"
          >
            <Card className="p-12 glass-effect border-primary/20 bg-gradient-to-br from-primary/10 to-accent/10 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">{t('products.custom_title')}</h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                {t('products.custom_subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="gradient-brand text-white hover:opacity-90">
                    {t('products.custom_cta')}
                  </Button>
                </Link>
                <Link to="/analytics">
                  <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
                    {t('products.analytics_cta')}
                  </Button>
                </Link>
              </div>
            </Card>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Products;